import {
  FocalEventPosterImage,
  getCoverEventPosterImage,
} from '@/features/events/shared/FocalEventPosterImage';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { type DiscoveryEvent } from '@/lib/api/apiTypes';
import { formatEventDayLabel, formatEventTime, getEventPalette } from '@/features/events/eventFormatting';
import { cn } from '@/lib/utils';
import { ArrowRightIcon, CalendarDaysIcon } from 'lucide-react-native';
import * as React from 'react';
import { Pressable, View } from 'react-native';

const PREVIEW_EVENT_COUNT = 4;

export function VenueUpcomingPreview({
  events,
  onOpenEvent,
  venueName,
}: {
  events: DiscoveryEvent[];
  onOpenEvent: (eventID: string) => void;
  venueName: string;
}) {
  const [showAll, setShowAll] = React.useState(false);

  if (events.length === 0) {
    return null;
  }

  const visibleEvents = showAll ? events : events.slice(0, PREVIEW_EVENT_COUNT);
  const remainingCount = events.length - PREVIEW_EVENT_COUNT;

  return (
    <View className="mx-4 mt-7 gap-3">
      <View className="flex-row items-end justify-between gap-3">
        <View className="min-w-0 flex-1 gap-1">
          <Text variant="small" className="uppercase tracking-[1.6px] text-primary">
            Calendar
          </Text>
          <Text className="text-xl font-bold text-foreground">This week at {venueName}</Text>
        </View>
        <Text variant="small" className="text-muted-foreground">
          {events.length} upcoming
        </Text>
      </View>

      <View className="overflow-hidden rounded-3xl border border-border/60 bg-card/70">
        {visibleEvents.map((event, index) => (
          <UpcomingPreviewRow
            key={event.id}
            event={event}
            isFirst={index === 0}
            onOpenEvent={onOpenEvent}
          />
        ))}

        {remainingCount > 0 ? (
          <View className="border-t border-border/50 px-3 py-3">
            <Button
              variant="outline"
              className="h-11 rounded-2xl"
              onPress={() => setShowAll((current) => !current)}>
              <Text>{showAll ? 'Show less' : `Show ${remainingCount} more`}</Text>
            </Button>
          </View>
        ) : null}
      </View>
    </View>
  );
}

function UpcomingPreviewRow({
  event,
  isFirst,
  onOpenEvent,
}: {
  event: DiscoveryEvent;
  isFirst: boolean;
  onOpenEvent: (eventID: string) => void;
}) {
  const handlePress = React.useCallback(() => {
    onOpenEvent(event.id);
  }, [event.id, onOpenEvent]);

  return (
    <Pressable
      className={cn(
        'flex-row items-center gap-3 px-3.5 py-3.5',
        !isFirst && 'border-t border-border/50'
      )}
      onPress={handlePress}>
      <UpcomingEventPoster event={event} />
      <View className="min-w-0 flex-1 gap-1">
        <Text className="text-base font-semibold leading-5 text-foreground" numberOfLines={2}>
          {event.title}
        </Text>
        <View className="flex-row flex-wrap items-center gap-x-2 gap-y-1">
          <Text variant="small" className="font-medium text-primary">
            {formatEventDayLabel(event.starts_at)}
          </Text>
          <Text variant="small" className="text-muted-foreground">
            {formatEventTime(event.starts_at)}
          </Text>
          {event.rsvp_count > 0 ? (
            <Text variant="small" className="text-muted-foreground">
              {event.rsvp_count} going
            </Text>
          ) : null}
        </View>
      </View>
      <Icon as={ArrowRightIcon} size={17} className="text-muted-foreground" />
    </Pressable>
  );
}

function UpcomingEventPoster({ event }: { event: DiscoveryEvent }) {
  const [base, accent] = getEventPalette(event.id);
  const [failedPosterURL, setFailedPosterURL] = React.useState<string | null>(null);
  const posterImage = React.useMemo(() => getCoverEventPosterImage(event), [event]);
  const posterURL = posterImage?.public_url ?? null;
  const showPoster = Boolean(posterURL) && failedPosterURL !== posterURL;

  return (
    <View className="h-[72px] w-[54px] overflow-hidden rounded-xl border border-border/60 bg-muted">
      {showPoster && posterImage ? (
        <FocalEventPosterImage
          image={posterImage}
          className="h-full w-full"
          onError={() => setFailedPosterURL(posterURL)}
        />
      ) : (
        <View className="h-full w-full items-center justify-center" style={{ backgroundColor: base }}>
          <View
            className="size-9 items-center justify-center rounded-full"
            style={{ backgroundColor: accent }}>
            <Icon as={CalendarDaysIcon} size={17} className="text-white" />
          </View>
        </View>
      )}
    </View>
  );
}
